/* eslint-disable react/prop-types */
/* eslint-disable camelcase */
import currency from "currency.js";
import {
  GetAccount200ResponsePositionsInner,
  InstrumentTypeResponse,
} from "../../codegen-api";
import { ITableColumn } from "../../interfaces/Table/TableColumn";
import { getOptionsTypeFromSymbol } from "../../utils/instruments";
import { getPositionIdentity } from "../../utils/positionIdentity";
import MarketCell from "../PortfolioSettings/Table/MarketCell";
import SortIcon from "../shared/SortIcon";
import { Align, SizeFillCell, TPSLCell } from "../shared/Table/style";
import TooltipExplanation from "../shared/Tooltip";
import { IModifiedPosition } from "./model";
import TpslCell from "./TpslCell";

export function buildMarketCol(
  t: any,
  tooltip: any,
  accountData: any
): ITableColumn<IModifiedPosition> {
  return {
    title: t("market"),
    accessor: "instrument_name",
    tooltip: tooltip("market"),
    Cell: ({ row }) => {
      const { option, instrument_name, instrument_id } = row.original;
      const settling = row.original.__tradeSyncSettling;
      const { expiry, strike } = option || {};
      const optionType = getOptionsTypeFromSymbol(instrument_name);
      // Prefer the leverage the position was actually opened at so the
      // badge and the ROE % can never drift. Fall back to the account
      // slider for instruments without an open position (shouldn't happen
      // in the Positions table, but kept for safety).
      const positionLeverage = (row.original as any).leverage;
      const sliderLeverage = accountData?.leverages?.find(
        (lev) => lev.instrument_id === instrument_id
      )?.leverage;
      const leverage = positionLeverage ?? sliderLeverage;

      if (optionType) {
        return (
          <MarketCell
            showSettlementInProgress
            settling={settling}
            instrumentName={instrument_name}
            optionType={optionType}
            expiry={expiry}
            strike={strike}
          />
        );
      }

      // Side is conveyed by the row's left bar + tint (see PositionTableRow),
      // Hyperliquid-style — not by coloring the coin text.
      return (
        <MarketCell
          showSettlementInProgress
          settling={settling}
          leverage={Number(leverage)}
          instrumentName={instrument_name}
        />
      );
    },
  };
}

export function PnlHeader({
  title,
  explanation,
  column,
}: {
  title: string;
  explanation: string;
  column?: any;
}) {
  return (
    <TooltipExplanation
      title={title}
      explanation={explanation}
      renderContent={({ ref, ...trigger }) => (
        <Align ref={ref as any} align="right" {...trigger}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            {title}
            {column?.canSort && (
              <SortIcon
                isSorted={column.isSorted}
                isSortedDesc={column.isSortedDesc}
              />
            )}
          </span>
        </Align>
      )}
    />
  );
}

export function buildIvCols(
  t: any,
  tooltip: any
): ITableColumn<IModifiedPosition>[] {
  const ivCols: ITableColumn<IModifiedPosition>[] = [
    {
      title: t("iv"),
      accessor: "iv",
      align: "right",
      tooltip: tooltip("iv"),
      valueExtractor: (value) =>
        value ? `${(Number(value) * 100).toFixed(2)}%` : "-",
    },
  ];
  return ivCols;
}

export function buildGreekCols(
  t: any,
  tooltip: any
): ITableColumn<IModifiedPosition>[] {
  const greekCols: ITableColumn<IModifiedPosition>[] = [
    {
      title: t("delta"),
      align: "right",
      // @ts-ignore
      accessor: "option.delta",
      tooltip: tooltip("delta"),
      valueExtractor: (delta) => Number(delta).toFixed(2),
    },
    {
      title: t("gamma"),
      align: "right",
      // @ts-ignore
      accessor: "option.gamma",
      tooltip: tooltip("gamma"),
      valueExtractor: (gamma) => Number(gamma).toFixed(2),
    },
    {
      title: t("vega"),
      align: "right",
      // @ts-ignore
      accessor: "option.vega",
      tooltip: tooltip("vega"),
      valueExtractor: (vega) => Number(vega).toFixed(2),
    },
    {
      title: t("theta"),
      align: "right",
      // @ts-ignore
      accessor: "option.theta",
      tooltip: tooltip("theta"),
      valueExtractor: (theta) => currency(theta).format(),
    },
  ];
  return greekCols;
}

export function buildPerpsCols(
  t: any,
  tooltip: any,
  /**
   * Opens TPSLForPositionModal. Omitted on chains without a TP/SL executor —
   * the column then falls back to the disabled "Coming soon" teaser.
   */
  onEditTpsl?: (positionId: string) => void
): ITableColumn<IModifiedPosition>[] {
  const hasPerpsCol: ITableColumn<IModifiedPosition>[] = [
    {
      id: "tpsl",
      title: "TP / SL",
      align: "right",
      tooltip: tooltip("tpslForPosition", {
        defaultValue: "Take profit and stop loss prices for this position",
      }),
      Cell: ({ row }: any) => {
        if (
          row.original.nestedOptionsData ||
          row.original.instrument_type !== InstrumentTypeResponse.Perpetual
        ) {
          return null;
        }
        if (onEditTpsl) {
          return (
            <TpslCell
              position={row.original}
              pricePrecision={row.original.pricePrecision}
              positionId={
                getPositionIdentity(row.original) || row.original.instrument_id
              }
              onEdit={onEditTpsl}
            />
          );
        }
        // No executor on this chain — keep the disabled teaser.
        return (
          <TooltipExplanation
            title="Take Profit / Stop Loss"
            explanation="Coming soon"
            renderContent={({ ref, ...triggerHandler }) => (
              <Align
                ref={ref as any}
                {...triggerHandler}
                align="right"
                style={{ opacity: 0.4, cursor: "not-allowed" }}
              >
                <TPSLCell>
                  <span>-</span>
                  <span>-</span>
                </TPSLCell>
              </Align>
            )}
          />
        );
      },
    },
    {
      title: t("liquidation_price"),
      accessor: "liquidation_price",
      align: "right",
      tooltip: tooltip("liquidationPrice"),
      Cell: ({ value, row }) => {
        if (row.original.nestedOptionsData) {
          return null;
        }
        const numValue = Number(value);
        const isValidPrice = value && !isNaN(numValue) && numValue > 0;
        const position = row.original as GetAccount200ResponsePositionsInner;
        const isCrossEstimate =
          position.liquidation_price_type === "cross_estimate" ||
          String(position.margin_type || "").toLowerCase() === "cross";
        return (
          <Align align="right">
            {isValidPrice ? (
              <SizeFillCell>
                <span>
                  {currency(numValue, {
                    precision: row.original.pricePrecision,
                  }).format()}
                </span>
                {isCrossEstimate ? (
                  <span>{t("estimated", { defaultValue: "Est." })}</span>
                ) : null}
              </SizeFillCell>
            ) : (
              "-"
            )}
          </Align>
        );
      },
    },
  ];
  return hasPerpsCol;
}
